import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { api } from '../lib/api'
import { useFactory } from './FactoryContext'

const TerminalContext = createContext(null)

/**
 * Contexto del terminal HMI (IP → línea).
 * Las rutas permitidas las sigue definiendo el usuario (RBAC).
 */
export function TerminalProvider({ children }) {
  const { setSelectedFactory } = useFactory()
  const [terminal, setTerminal] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/'
  const isPublicBootPath = pathname === '/login' || pathname.startsWith('/mobile/')

  const loadTerminalContext = useCallback(async () => {
    if (isPublicBootPath) {
      setTerminal(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await api.get('/api/terminal/context')
      if (data?.bound && data?.hmiMode) {
        setTerminal(data)
        if (data.factory) {
          setSelectedFactory(data.factory)
        }
      } else {
        setTerminal(null)
      }
    } catch (err) {
      console.error('[TerminalContext] Error cargando contexto del terminal:', err)
      setTerminal(null)
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [setSelectedFactory, isPublicBootPath])

  useEffect(() => {
    loadTerminalContext()
  }, [loadTerminalContext])

  const value = useMemo(
    () => ({
      terminal,
      isHmiMode: Boolean(terminal?.bound && terminal?.hmiMode),
      loading,
      error,
      reload: loadTerminalContext,
    }),
    [terminal, loading, error, loadTerminalContext]
  )

  return (
    <TerminalContext.Provider value={value}>
      {children}
    </TerminalContext.Provider>
  )
}

export function useTerminal() {
  const context = useContext(TerminalContext)
  if (context == null) {
    throw new Error('useTerminal must be used within a TerminalProvider')
  }
  return context
}
